<script setup>
import { ref, computed, nextTick } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import MultiselectDropdownItems from 'shared/components/ui/MultiselectDropdownItems.vue';

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
  },
  selectedItem: {
    type: Object,
    default: null,
  },
  maxHeight: {
    type: String,
    default: '12rem',
  },
  hasThumbnail: {
    type: Boolean,
    default: true,
  },
  hideSearch: {
    type: Boolean,
    default: true,
  },
  teleportTo: {
    type: String,
    default: 'body',
  },
});

const emit = defineEmits(['select', 'close', 'open']);

const showMenu = ref(false);
const menuPosition = ref({ x: 0, y: 0 });
const triggerElement = ref(null);

// The host ContextMenu ignores `to` and always teleports to <body>. Inside a modal
// <dialog> (browser top layer) that puts the menu under the dialog, invisible and
// unclickable: the step and priority pickers in the card modal footer never opened.
// Two more host constraints shape the fallback:
// - the host Dialog does not render its `id`, so a `#id` target may not exist;
// - it closes on any click outside its inner <form> (it ignores
//   `ignore-click-outside`), so the menu must live inside that form.
// The target is resolved when the menu opens: the given selector if it exists,
// otherwise the form of the <dialog> holding the trigger; outside dialogs the host menu.
const menuTarget = ref(null);
const inDialog = computed(() => Boolean(menuTarget.value));

const resolveMenuTarget = trigger => {
  const bySelector =
    props.teleportTo !== 'body'
      ? document.querySelector(props.teleportTo)
      : null;
  const dialog = bySelector || trigger?.closest?.('dialog');
  if (!dialog) return null;
  return dialog.querySelector('form') || dialog;
};
const menuRef = ref(null);
const isPlaced = ref(false);
const menuStyle = computed(() => ({
  left: `${menuPosition.value.x}px`,
  top: `${menuPosition.value.y}px`,
  visibility: isPlaced.value ? 'visible' : 'hidden',
}));

// The dialog form is transformed and clips its overflow, so a `fixed` child is laid
// out from the form's origin, not the viewport, and is cut at the form's edges (and
// the dialog animates while opening, so that origin moves). Once rendered, the menu
// is put at (0, 0) to read where that origin is right now; it is then placed under
// the trigger, or above it when it does not fit, and only then made visible.
const placeMenu = () => {
  const menu = menuRef.value;
  if (!menu || !menuTarget.value || !triggerElement.value) return;
  const PADDING = 8;
  menu.style.left = '0px';
  menu.style.top = '0px';
  const origin = menu.getBoundingClientRect();
  const { width, height } = origin;
  const trigger = triggerElement.value.getBoundingClientRect();
  const bounds = menuTarget.value.getBoundingClientRect();
  const bottomLimit = Math.min(bounds.bottom, window.innerHeight) - PADDING;
  const topLimit = Math.max(bounds.top, 0) + PADDING;
  const rightLimit = Math.min(bounds.right, window.innerWidth) - PADDING;

  let x = trigger.left;
  let y = trigger.bottom + 8;
  if (y + height > bottomLimit) {
    y = Math.max(topLimit, trigger.top - height - 8);
  }
  if (x + width > rightLimit) {
    x = Math.max(bounds.left + PADDING, rightLimit - width);
  }
  menuPosition.value = { x: x - origin.left, y: y - origin.top };
  menu.style.left = `${menuPosition.value.x}px`;
  menu.style.top = `${menuPosition.value.y}px`;
  isPlaced.value = true;
};

const close = () => {
  showMenu.value = false;
  emit('close');
};

const open = event => {
  triggerElement.value = event.currentTarget || event.target;

  if (showMenu.value) {
    close();
    return;
  }

  const rect = triggerElement.value.getBoundingClientRect();
  menuTarget.value = resolveMenuTarget(triggerElement.value);
  menuPosition.value = { x: rect.left, y: rect.bottom + 8 };
  isPlaced.value = !menuTarget.value;
  showMenu.value = true;
  emit('open');
  if (inDialog.value) nextTick(placeMenu);
};

const onSelect = item => {
  emit('select', item);
  close();
};
</script>

<template>
  <slot name="trigger" :open="open" :is-open="showMenu" />

  <ContextMenu
    v-if="showMenu && !inDialog"
    :x="menuPosition.x"
    :y="menuPosition.y"
    @close="close"
  >
    <div
      class="w-48 overflow-hidden rounded-lg bg-n-alpha-3 backdrop-blur-[100px] border border-n-strong dark:border-n-strong p-2 shadow-lg cursor-default"
    >
      <MultiselectDropdownItems
        :options="options"
        :selected-items="selectedItem ? [selectedItem] : []"
        :hide-search="hideSearch"
        :max-height="maxHeight"
        :has-thumbnail="hasThumbnail"
        @select="onSelect"
      />
    </div>
  </ContextMenu>
  <Teleport v-else-if="showMenu" :to="menuTarget">
    <OnClickOutside :options="{ ignore: [triggerElement] }" @trigger="close">
      <div
        ref="menuRef"
        class="fixed z-[9999]"
        :style="menuStyle"
        data-test="kanban-context-dropdown-menu"
        @keydown.esc.stop.prevent="close"
        @click.prevent
      >
        <div
          class="w-48 overflow-hidden rounded-lg bg-n-alpha-3 backdrop-blur-[100px] border border-n-strong dark:border-n-strong p-2 shadow-lg cursor-default"
        >
          <MultiselectDropdownItems
            :options="options"
            :selected-items="selectedItem ? [selectedItem] : []"
            :hide-search="hideSearch"
            :max-height="maxHeight"
            :has-thumbnail="hasThumbnail"
            @select="onSelect"
          />
        </div>
      </div>
    </OnClickOutside>
  </Teleport>
</template>
